import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Switch from '@mui/material/Switch';
import SettingsDropdown from 'common/SettingsDropdown';
import { SettingsMenuItem } from 'common/SettingsDropdown/SettingsDropdown';
import { Stack, Spacer } from 'common/ui/Layout';
import Text from 'common/ui/Text';
import { MODALITIES } from 'config/constants';
import {
  ImpressionModalityScope,
  type ImpressionGenerator,
  type ReporterSettings,
} from 'generated/graphql';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { Colors } from 'styles';
import { SpecificImpressionModalities } from './SpecificImpressionModalities';
import { css } from 'styled-components';

type ReporterImpressionGenerationSettingsProps = {
  reporterSettings: Nullable<ReporterSettings>;
  handleSaveSettings: (settings: { impressionGenerator: ImpressionGenerator }) => Promise<void>;
};

export const ReporterImpressionGenerationSettings = ({
  reporterSettings,
  handleSaveSettings,
}: ReporterImpressionGenerationSettingsProps): React.ReactElement => {
  const [isImpressionModalityScopeEnabled] = useFeatureFlagEnabled(
    FF.REPORTER_AUTO_IMPRESSIONS_MODALITY_SCOPE
  );
  const impressionGenerator = reporterSettings?.impressionGenerator;
  const modalityScope = impressionGenerator?.modalityScope ?? ImpressionModalityScope.Specific;

  return (
    <Stack vertical space="xsmall">
      <Text variant="display1" display="block">
        Impression Generation
      </Text>
      <Stack alignY="center">
        <Text>Enable Impression Generation</Text>
        <Spacer />
        <Switch
          data-testid="impression-generator-switch"
          checked={reporterSettings?.impressionGenerator?.enabled}
          onChange={(event) => {
            handleSaveSettings({
              impressionGenerator: {
                __typename: 'ImpressionGenerator',
                enabled: event.target.checked,
                automatic: impressionGenerator?.automatic ?? false,
                modalityScope,
                modalities: impressionGenerator?.modalities ?? [],
              },
            });
          }}
        />
      </Stack>
      {impressionGenerator?.enabled === true &&
        (isImpressionModalityScopeEnabled ? (
          <Stack vertical space="xsmall" css={{ paddingLeft: '16px' }}>
            <Stack alignY="center">
              <Text variant="body1">Modalities</Text>
              <Spacer />
              <Stack
                alignY="center"
                space="medium"
                css={css`
                  flex-wrap: wrap;
                `}
              >
                <RadioGroup
                  row
                  aria-label="impression generation modality scope"
                  name="impression-generation-modality-scope"
                  value={modalityScope}
                  onChange={(event) => {
                    handleSaveSettings({
                      impressionGenerator: {
                        ...impressionGenerator,
                        modalityScope: event.target.value as ImpressionModalityScope,
                      },
                    });
                  }}
                >
                  <FormControlLabel
                    value={ImpressionModalityScope.All}
                    control={<Radio color="primary" />}
                    label={<Text variant="body1">All modalities</Text>}
                  />
                  <FormControlLabel
                    value={ImpressionModalityScope.Specific}
                    control={<Radio color="primary" />}
                    label={<Text variant="body1">Specific modalities</Text>}
                  />
                </RadioGroup>
              </Stack>
            </Stack>
            {modalityScope === ImpressionModalityScope.Specific && (
              <SpecificImpressionModalities
                modalities={impressionGenerator.modalities}
                onSelectAllModalities={() => {
                  handleSaveSettings({
                    impressionGenerator: {
                      ...impressionGenerator,
                      modalityScope: ImpressionModalityScope.All,
                    },
                  });
                }}
                onToggleModality={(modality) => {
                  const nextModalities = impressionGenerator.modalities.includes(modality)
                    ? impressionGenerator.modalities.filter((value) => value !== modality)
                    : [...impressionGenerator.modalities, modality];

                  handleSaveSettings({
                    impressionGenerator: {
                      ...impressionGenerator,
                      modalities: nextModalities,
                    },
                  });
                }}
              />
            )}
          </Stack>
        ) : (
          <Stack alignY="center" alignX="start" space="medium" css={{ paddingLeft: '16px' }}>
            <Text variant="body1">
              Generate Impression on first navigation to Impression section for
            </Text>
            <SettingsDropdown
              multiple={true}
              displayEmpty={true}
              value={impressionGenerator.modalities}
              onSelectedChange={(value) => {
                if (Array.isArray(value)) {
                  handleSaveSettings({
                    impressionGenerator: {
                      ...impressionGenerator,
                      modalities: value,
                    },
                  });
                }
              }}
              data-testid="impression-modalities-dropdown"
            >
              {MODALITIES.map((option) => (
                <SettingsMenuItem
                  id={`select-impression-modality-item-${option.value}`}
                  value={option.value}
                  key={option.value}
                  data-testid={`${option.value}-impression-modality-item`}
                >
                  <Checkbox
                    sx={{ color: Colors.blue4 }}
                    color="primary"
                    checked={impressionGenerator.modalities.includes(option.value)}
                  />
                  {option.name}
                </SettingsMenuItem>
              ))}
            </SettingsDropdown>
          </Stack>
        ))}
    </Stack>
  );
};
